// framework/common/schemas/collections/agentConfigs.js
import { z } from "zod";

const AgentPersonalitySchema = z.object({
    profession: z.string().optional().default('general_assistant'),
    style: z.string().optional().default('neutral'),
    gender: z.string().optional().default('neutral'),
    verbosity: z.enum(['concise', 'balanced', 'detailed']).optional().default('balanced'),
    defaultMood: z.string().optional().default('neutral'),
    adaptMood: z.boolean().optional().default(true),
    notes: z.string().optional().nullable(),
}).nullable().optional();

const preprocessBool = (defaultValue = false) => (val) => {
    if (typeof val === 'object' && val !== null) {
        return !!(val.enabled ?? val.participate ?? val.canCreate ?? defaultValue);
    }
    if (typeof val === 'boolean') return val;
    if (val === undefined || val === null) return defaultValue;
    return !!val;
};

export const AgentCapabilitiesSchema = z.object({
    sandbox: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    browserSandbox: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    vpcManagement: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    tasks: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    playbooks: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    scheduler: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    secrets: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    webApi: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    knowledgeBase: z.preprocess(preprocessBool(true), z.boolean().default(true)),
    knowledgeBaseWebSearch: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    knowledgeBaseRemember: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    voiceEnabled: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    phoneEnabled: z.preprocess(preprocessBool(false), z.boolean().default(false)),
    skills: z.preprocess(preprocessBool(true), z.boolean().default(true)),
    commands: z.preprocess(preprocessBool(true), z.boolean().default(true)),
    documentManagement: z.preprocess(preprocessBool(false), z.boolean().default(false)),
}).nullable().optional();

export const AgentTasksConfigSchema = z.object({
    participate: z.boolean().optional().default(false),
    manageBoards: z.boolean().optional().default(false),
    allowedBoards: z.array(z.string()).optional().default([]),
}).optional().default({ participate: false, manageBoards: false, allowedBoards: [] });

export const AgentPlaybooksConfigSchema = z.object({
    canCreate: z.boolean().optional().default(false),
    allowedTags: z.array(z.string()).optional().default([]),
}).optional().default({ canCreate: false, allowedTags: [] });

export const AgentSchedulerConfigSchema = z.object({
    scope: z.enum(['own', 'subscription', 'project']).optional().default('own'),
}).optional().default({ scope: 'own' });

export const AgentSecretsConfigSchema = z.object({
    scope: z.enum(['agent', 'subscription', 'project']).optional().default('agent'),
}).optional().default({ scope: 'agent' });

export const AgentWebApiConfigSchema = z.object({
    accessMode: z.enum(['all', 'whitelist', 'blacklist']).optional().default('all'),
    allowedApis: z.array(z.string()).optional().default([]),
    blockedApis: z.array(z.string()).optional().default([]),
    manageRegistry: z.boolean().optional().default(false),
}).optional().default({ accessMode: 'all', allowedApis: [], blockedApis: [], manageRegistry: false });

export const AgentKnowledgeBaseConfigSchema = z.object({
    defaultScope: z.enum(['conversation', 'contact', 'global']).optional().default('global'),
    defaultSource: z.enum(['knowledge', 'web', 'all']).optional().default('knowledge'),
    preferredEngine: z.enum(['auto', 'brave', 'searxng', 'google']).optional().default('auto'),
    allowedDocTypes: z.array(z.string()).optional().default([]),
    allowedCollections: z.array(z.string()).optional().default([]),
    blockedCollections: z.array(z.string()).optional().default([]),
    domainWhitelist: z.array(z.string()).optional().default([]),
    domainBlacklist: z.array(z.string()).optional().default([]),
    maxWriteScope: z.enum(['conversation', 'contact', 'global']).optional().default('contact'),
    crossSessionRetrieval: z.boolean().optional().default(false),
    crossUserRetrieval: z.boolean().optional().default(false),
}).optional().nullable();

export const AgentSpeechRealtimeConfigSchema = z.object({
    engineProvider: z.enum(['custom', 'openai_realtime', 'gemini_realtime']).optional().default('custom'),
    ttsModel: z.string().optional().nullable(),
    selectedVoice: z.string().optional().nullable(),
    transcriptionModel: z.string().optional().nullable(),
    openAIRealtimeModel: z.string().optional().nullable(),
    geminiRealtimeModel: z.string().optional().nullable(),
    voice: z.string().optional().nullable().default('alloy'),
}).optional().default({ engineProvider: 'custom', voice: 'alloy' });

export const AgentTelephonyConfigSchema = z.object({
    speakFirstOutgoing: z.enum(['agent_first', 'wait_for_callee', 'auto']).optional().default('agent_first'),
    allowOutgoingCalls: z.boolean().optional().default(false),
    allowIncomingCalls: z.boolean().optional().default(false),
    allowGroupParticipation: z.boolean().optional().default(false),
    forkNewInstanceForCalls: z.boolean().optional().default(false),
}).optional().default({ speakFirstOutgoing: 'agent_first', allowOutgoingCalls: false, allowIncomingCalls: false, allowGroupParticipation: false, forkNewInstanceForCalls: false });

export const AgentSkillsConfigSchema = z.object({
    manageSkills: z.boolean().optional().default(false),
    accessMode: z.enum(['all', 'whitelist', 'blacklist']).optional().default('all'),
    allowed: z.array(z.string()).optional().default([]),
    blocked: z.array(z.string()).optional().default([]),
}).optional().default({ manageSkills: false, accessMode: 'all', allowed: [], blocked: [] });

export const AgentCommandsConfigSchema = z.object({
    allowed: z.array(z.string()).optional().default([]),
    blocked: z.array(z.string()).optional().default([]),
    discoverableByDefault: z.boolean().optional().default(false),
}).optional().default({ allowed: [], blocked: [], discoverableByDefault: false });

export const AgentDocumentManagementConfigSchema = z.object({
    read: z.boolean().optional().default(true),
    write: z.boolean().optional().default(false),
    allowedCollections: z.array(z.string()).optional().default([]),
}).optional().default({ read: true, write: false, allowedCollections: [] });

const DynamicContextSectionSchema = z.object({
    surface: z.enum([
        'stable_prompt',
        'durable_hidden',
        'changed_hidden',
        'ephemeral',
        'tool',
        'off'
    ]).optional().default('tool'),
    renderPrompt: z.string().optional().nullable(),
    detail: z.enum(['summary', 'full']).optional().default('summary'),
    freshness: z.enum(['always', 'digest', 'ttl', 'manual']).optional().default('digest'),
    maxTokens: z.number().int().positive().optional(),
    filters: z.record(z.any()).optional().default({}),
}).passthrough();

const DynamicContextConfigSchema = z.object({
    enabled: z.boolean().optional().default(true),
    sections: z.record(DynamicContextSectionSchema).optional().default({}),
}).passthrough();

const MemoryCompactionConfigSchema = z.object({
    enabled: z.boolean().optional().default(true),
    mode: z.enum(['manual', 'threshold', 'automatic']).optional().default('threshold'),
    thresholdInputTokens: z.number().int().positive().optional().default(100000),
    targetSummaryTokens: z.number().int().positive().optional().default(4000),
    preserveRecentTurns: z.number().int().nonnegative().optional().default(20),
    preserveToolGroups: z.boolean().optional().default(true),
    preserveHiddenContext: z.boolean().optional().default(false),
    manualAllowed: z.boolean().optional().default(true),
}).passthrough();

const AgentMemoryConfigSchema = z.object({
    manager: z.enum(['default', 'simple']).optional().default('default'),
    compaction: MemoryCompactionConfigSchema.optional().default({}),
}).passthrough();

const AgentConfigSchema = z.object({
    _id: z.string().optional().describe("MongoDB ObjectId"),
    subscriptionId: z.string().optional().nullable().describe("ID of the subscription this agent configuration belongs to"),
    userId: z.string().describe("ID of the user who owns this agent configuration"),
    name: z.string().min(1, "Agent name is required."),
    description: z.string().optional().nullable(),

    agentClass: z.enum(["AIAgent", "RealtimeAIAgent"])
        .default("AIAgent")
        .describe("The base class for the agent instance. AIAgent for most, RealtimeAIAgent for streaming."),

    hostService: z.string().optional().nullable()
        .describe("The service name where this agent should run (e.g., 'WorkforceManager'). If null, runs locally."),

    contextScope: z.enum(['conversation', 'user', 'global']).optional().default('conversation')
        .describe("The memory scope for the agent."),

    communicationModes: z.array(z.enum(["direct", "multiChannelMessage", "phoneCall", "omnix_callback"]))
        .default(["direct"])
        .describe("An array of communication channels the agent supports."),

    model: z.string().min(1, "Primary AI Model is required.").describe("The ID of the primary AI model used by the agent."),

    promptSource: z.enum(["file", "text"])
        .default("text")
        .describe("Source of the system prompt."),
    promptFile: z.string().optional().nullable()
        .describe("Path to the prompt file (if promptSource is 'file')."),
    promptText: z.string().optional().nullable()
        .describe("The actual system prompt text (if promptSource is 'text')."),

    personalityEnabled: z.boolean().optional().default(false),
    personality: AgentPersonalitySchema,

    role: z.string().optional().nullable().describe("Assigned role for the agent."),
    systemRole: z.string().optional().nullable().describe("System permissions role."),

    tools: z.array(z.string()).optional().default([])
        .describe("List of tool names available to this agent."),

    capabilities: AgentCapabilitiesSchema,

    tasks: AgentTasksConfigSchema,
    playbooks: AgentPlaybooksConfigSchema,
    scheduler: AgentSchedulerConfigSchema,
    secrets: AgentSecretsConfigSchema,
    webApi: AgentWebApiConfigSchema,
    knowledgeBase: AgentKnowledgeBaseConfigSchema,
    speechRealtime: AgentSpeechRealtimeConfigSchema,
    telephony: AgentTelephonyConfigSchema,
    skills: AgentSkillsConfigSchema,
    commands: AgentCommandsConfigSchema,
    documentManagement: AgentDocumentManagementConfigSchema,

    fileContext: z.record(z.any()).optional().nullable()
        .describe("Configuration for loading file-based context into the prompt."),

    dynamicContext: DynamicContextConfigSchema.optional().default({ enabled: true, sections: {} })
        .describe("Configures Dynamic Context sections, surfaces, render prompts, filters, and token bounds."),

    memory: AgentMemoryConfigSchema.optional().default({ manager: 'default', compaction: {} })
        .describe("Conversation memory manager and compaction policy."),

    saveChatHistory: z.boolean().default(true),
    enableDeepWork: z.boolean().optional().default(false),
    messagingContactId: z.string().optional().nullable(),
    avatarUrl: z.string().optional().nullable(),
    realtime: z.boolean().optional().default(false),

    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});

export function normalizeAgentConfig(data) {
    if (!data || typeof data !== 'object') return data;

    const capabilities = data.capabilities ? { ...data.capabilities } : {};

    // 1. Sandbox, BrowserSandbox, VpcManagement
    capabilities.sandbox = data.capabilities?.sandbox ?? data.sandbox ?? false;
    capabilities.browserSandbox = data.capabilities?.browserSandbox ?? data.browserSandbox ?? false;
    capabilities.vpcManagement = data.capabilities?.vpcManagement ?? data.vpcManagement ?? false;

    // 2. Tasks
    const oldTasks = data.capabilities?.tasks || {};
    const tasks = {
        participate: typeof oldTasks === 'object' ? (oldTasks.participate ?? (data.tasks?.participate ?? false)) : (data.tasks?.participate ?? false),
        manageBoards: typeof oldTasks === 'object' ? (oldTasks.manageBoards ?? (data.tasks?.manageBoards ?? false)) : (data.tasks?.manageBoards ?? false),
        allowedBoards: typeof oldTasks === 'object' ? (oldTasks.allowedBoards ?? (data.tasks?.allowedBoards || [])) : (data.tasks?.allowedBoards || []),
    };
    capabilities.tasks = typeof oldTasks === 'boolean'
        ? oldTasks
        : !!(oldTasks.enabled ?? oldTasks.participate ?? oldTasks.manageBoards ?? (data.capabilities?.tasks ? true : false));

    // 3. Playbooks
    const oldPlaybooks = data.capabilities?.playbooks || {};
    const playbooks = {
        canCreate: typeof oldPlaybooks === 'object' ? (oldPlaybooks.canCreate ?? (data.playbooks?.canCreate ?? false)) : (data.playbooks?.canCreate ?? false),
        allowedTags: typeof oldPlaybooks === 'object' ? (oldPlaybooks.allowedTags ?? (data.playbooks?.allowedTags || [])) : (data.playbooks?.allowedTags || []),
    };
    capabilities.playbooks = typeof oldPlaybooks === 'boolean'
        ? oldPlaybooks
        : !!(oldPlaybooks.enabled ?? oldPlaybooks.canCreate ?? (data.capabilities?.playbooks ? true : false));

    // 4. Scheduler
    const oldScheduler = data.capabilities?.scheduler || {};
    const scheduler = {
        scope: typeof oldScheduler === 'object' ? (oldScheduler.scope ?? (data.scheduler?.scope ?? 'own')) : (data.scheduler?.scope ?? 'own'),
    };
    capabilities.scheduler = typeof oldScheduler === 'boolean'
        ? oldScheduler
        : !!(oldScheduler.enabled ?? (data.capabilities?.scheduler ? true : false));

    // 5. Secrets
    const oldSecrets = data.capabilities?.secrets || {};
    const secrets = {
        scope: typeof oldSecrets === 'object' ? (oldSecrets.scope ?? (data.secrets?.scope ?? 'agent')) : (data.secrets?.scope ?? 'agent'),
    };
    capabilities.secrets = typeof oldSecrets === 'boolean'
        ? oldSecrets
        : !!(oldSecrets.enabled ?? (data.capabilities?.secrets ? true : false));

    // 6. Web API
    const oldWebApi = data.capabilities?.webApi || {};
    const webApi = {
        accessMode: typeof oldWebApi === 'object' ? (oldWebApi.accessMode ?? (data.webApi?.accessMode ?? 'all')) : (data.webApi?.accessMode ?? 'all'),
        allowedApis: typeof oldWebApi === 'object' ? (oldWebApi.allowedApis ?? (data.webApi?.allowedApis || [])) : (data.webApi?.allowedApis || []),
        blockedApis: typeof oldWebApi === 'object' ? (oldWebApi.blockedApis ?? (data.webApi?.blockedApis || [])) : (data.webApi?.blockedApis || []),
        manageRegistry: typeof oldWebApi === 'object' ? (oldWebApi.manageRegistry ?? (data.webApi?.manageRegistry ?? false)) : (data.webApi?.manageRegistry ?? false),
    };
    capabilities.webApi = typeof oldWebApi === 'boolean'
        ? oldWebApi
        : !!(oldWebApi.enabled ?? (data.capabilities?.webApi ? true : false));

    // 7. Knowledge Base
    const oldKB = data.knowledgeBase || {};
    const knowledgeBase = {
        defaultScope: oldKB.defaultScope ?? 'global',
        defaultSource: oldKB.defaultSource ?? 'knowledge',
        preferredEngine: oldKB.preferredEngine ?? 'auto',
        allowedDocTypes: oldKB.allowedDocTypes || [],
        allowedCollections: oldKB.allowedCollections || [],
        blockedCollections: oldKB.blockedCollections || [],
        domainWhitelist: oldKB.domainWhitelist || [],
        domainBlacklist: oldKB.domainBlacklist || [],
        maxWriteScope: oldKB.maxWriteScope ?? 'contact',
        crossSessionRetrieval: oldKB.crossSessionRetrieval ?? false,
        crossUserRetrieval: oldKB.crossUserRetrieval ?? false,
    };
    capabilities.knowledgeBase = data.capabilities?.knowledgeBase ?? (oldKB.enabled ?? true);
    capabilities.knowledgeBaseWebSearch = data.capabilities?.knowledgeBaseWebSearch ?? (oldKB.webSearchEnabled ?? false);
    capabilities.knowledgeBaseRemember = data.capabilities?.knowledgeBaseRemember ?? (oldKB.rememberEnabled ?? false);

    // 8. Voice & Telephony Enablement
    capabilities.voiceEnabled = data.capabilities?.voiceEnabled ?? data.voiceEnabled ?? false;
    capabilities.phoneEnabled = data.capabilities?.phoneEnabled ?? data.phoneEnabled ?? false;

    // 9. Speech Realtime Config
    const speechRealtime = {
        engineProvider: data.speechRealtime?.engineProvider ?? data.voiceProvider ?? 'custom',
        ttsModel: data.speechRealtime?.ttsModel ?? data.ttsModel ?? null,
        selectedVoice: data.speechRealtime?.selectedVoice ?? data.selectedVoice ?? null,
        transcriptionModel: data.speechRealtime?.transcriptionModel ?? data.transcriptionModel ?? null,
        openAIRealtimeModel: data.speechRealtime?.openAIRealtimeModel ?? data.openAIRealtimeModel ?? null,
        geminiRealtimeModel: data.speechRealtime?.geminiRealtimeModel ?? data.geminiRealtimeModel ?? null,
        voice: data.speechRealtime?.voice ?? data.voice ?? 'alloy',
    };

    // 10. Telephony Config
    const telephony = {
        speakFirstOutgoing: data.telephony?.speakFirstOutgoing ?? data.speakFirstOutgoing ?? 'agent_first',
        allowOutgoingCalls: data.telephony?.allowOutgoingCalls ?? data.allowOutgoingCalls ?? false,
        allowIncomingCalls: data.telephony?.allowIncomingCalls ?? data.allowIncomingCalls ?? false,
        allowGroupParticipation: data.telephony?.allowGroupParticipation ?? data.allowGroupParticipation ?? false,
        forkNewInstanceForCalls: data.telephony?.forkNewInstanceForCalls ?? data.forkNewInstanceForCalls ?? false,
    };

    // 11. Skills
    const oldSkillsCap = data.capabilities?.skills || {};
    const skills = {
        manageSkills: typeof oldSkillsCap === 'object' ? (oldSkillsCap.manageSkills ?? (data.skills?.manageSkills ?? false)) : (data.skills?.manageSkills ?? false),
        accessMode: data.skills?.accessMode ?? data.skillAccessMode ?? 'all',
        allowed: data.skills?.allowed ?? data.allowedSkills ?? [],
        blocked: data.skills?.blocked ?? data.blockedSkills ?? [],
    };
    capabilities.skills = typeof oldSkillsCap === 'boolean' ? oldSkillsCap : !!(oldSkillsCap.enabled ?? (data.capabilities?.skills ?? true));

    // 12. Commands
    const oldCommands = data.commands || {};
    const commands = {
        allowed: oldCommands.allowed ?? [],
        blocked: oldCommands.blocked ?? [],
        discoverableByDefault: oldCommands.discoverableByDefault ?? false,
    };
    capabilities.commands = data.capabilities?.commands ?? (oldCommands.enabled ?? true);

    // 13. Document Management
    const oldDocMgmt = data.documentManagement || {};
    const documentManagement = {
        read: oldDocMgmt.read ?? true,
        write: oldDocMgmt.write ?? false,
        allowedCollections: oldDocMgmt.allowedCollections || [],
    };
    capabilities.documentManagement = data.capabilities?.documentManagement ?? false;

    // Build the final normalized config object
    const normalized = {
        ...data,
        capabilities,
        tasks,
        playbooks,
        scheduler,
        secrets,
        webApi,
        knowledgeBase,
        speechRealtime,
        telephony,
        skills,
        commands,
        documentManagement,
    };

    // Remove obsolete top-level fields
    delete normalized.voiceEnabled;
    delete normalized.phoneEnabled;
    delete normalized.voiceProvider;
    delete normalized.ttsModel;
    delete normalized.selectedVoice;
    delete normalized.transcriptionModel;
    delete normalized.openAIRealtimeModel;
    delete normalized.geminiRealtimeModel;
    delete normalized.voice;
    delete normalized.speakFirstOutgoing;
    delete normalized.allowOutgoingCalls;
    delete normalized.allowIncomingCalls;
    delete normalized.allowGroupParticipation;
    delete normalized.forkNewInstanceForCalls;
    delete normalized.skillAccessMode;
    delete normalized.allowedSkills;
    delete normalized.blockedSkills;

    return normalized;
}

export default AgentConfigSchema;
